import { useCallback, useEffect, useRef } from "react";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { resolveTtsVoiceId, ttsService, type TtsVoiceOption } from "@/services/ttsService";

const MAX_TTS_CHARS = 5000;

function toSpeakableText(content: string): string {
  return content
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[#*_`~]/g, "")
    .substring(0, MAX_TTS_CHARS);
}

export function useChatTTS() {
  const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
  const [isSpeechPaused, setIsSpeechPaused] = useState(false);
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoiceUri, setSelectedVoiceUri] = useState<string>("");
  const [speechRate, setSpeechRate] = useState<number>(1.0);
  const [ttsPrefsLoaded, setTtsPrefsLoaded] = useState(false);
  const [ttsVoices, setTtsVoices] = useState<TtsVoiceOption[]>([]);
  const [selectedTtsVoiceId, setSelectedTtsVoiceId] = useState<string>("");
  const [ttsLoadingMessageId, setTtsLoadingMessageId] = useState<string | null>(null);
  const [ttsErrorMessageId, setTtsErrorMessageId] = useState<string | null>(null);
  const [ttsErrorMessage, setTtsErrorMessage] = useState<string | null>(null);
  const prefsAppliedRef = useRef(false);

  const currentUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrlRef = useRef<string | null>(null);
  const speakSessionRef = useRef(0);
  const { profile, isLoading } = useUserProfile();

  useEffect(() => {
    if (prefsAppliedRef.current || isLoading) return;
    if (profile?.preferences) {
      const prefs = profile.preferences as {
        ttsVoiceUri?: string;
        ttsSpeechRate?: number;
        ttsVoiceId?: string;
        elevenlabsVoiceId?: string;
      };
      if (prefs.ttsVoiceUri) setSelectedVoiceUri(prefs.ttsVoiceUri);
      if (prefs.ttsSpeechRate) setSpeechRate(prefs.ttsSpeechRate);
      const voiceId = resolveTtsVoiceId(prefs);
      if (voiceId) setSelectedTtsVoiceId(voiceId);
    }
    prefsAppliedRef.current = true;
    setTtsPrefsLoaded(true);
  }, [profile, isLoading, setSelectedVoiceUri, setSpeechRate, setSelectedTtsVoiceId, setTtsPrefsLoaded]);

  useEffect(() => {
    let cancelled = false;
    ttsService
      .getVoices()
      .then((voices) => {
        if (!cancelled) setTtsVoices(voices);
      })
      .catch(() => {
        /* browser fallback stays available */
      });
    return () => {
      cancelled = true;
    };
  }, [setTtsVoices]);

  const lastSavedPrefsRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ttsPrefsLoaded) return;
    const snapshot = JSON.stringify({
      ttsVoiceUri: selectedVoiceUri,
      ttsSpeechRate: speechRate,
      ttsVoiceId: selectedTtsVoiceId,
    });
    if (lastSavedPrefsRef.current === null) {
      lastSavedPrefsRef.current = snapshot;
      return;
    }
    if (lastSavedPrefsRef.current === snapshot) return;
    const timer = setTimeout(() => {
      lastSavedPrefsRef.current = snapshot;
      ttsService
        .savePreferences({
          ttsVoiceUri: selectedVoiceUri || undefined,
          ttsSpeechRate: speechRate,
          ttsVoiceId: selectedTtsVoiceId || undefined,
        })
        .catch((err) => console.error("Failed to save TTS preferences:", err));
    }, 1000);
    return () => clearTimeout(timer);
  }, [ttsPrefsLoaded, selectedVoiceUri, speechRate, selectedTtsVoiceId]);

  useEffect(() => {
    const loadVoices = () => {
      if ("speechSynthesis" in window) {
        const voices = speechSynthesis.getVoices();
        const naturalVoices = voices
          .filter(
            (v) =>
              v.lang.startsWith("en") ||
              v.lang.startsWith("es") ||
              v.lang.startsWith("fr") ||
              v.lang.startsWith("de")
          )
          .sort((a, b) => {
            const aScore =
              a.name.toLowerCase().includes("natural") ||
              a.name.toLowerCase().includes("premium") ||
              a.name.toLowerCase().includes("neural")
                ? 1
                : 0;
            const bScore =
              b.name.toLowerCase().includes("natural") ||
              b.name.toLowerCase().includes("premium") ||
              b.name.toLowerCase().includes("neural")
                ? 1
                : 0;
            return bScore - aScore;
          });
        setAvailableVoices(naturalVoices.length > 0 ? naturalVoices : voices);

        if (ttsPrefsLoaded && !selectedVoiceUri && naturalVoices.length > 0) {
          const defaultVoice =
            naturalVoices.find(
              (v) =>
                v.name.toLowerCase().includes("natural") ||
                v.name.toLowerCase().includes("neural") ||
                v.name.toLowerCase().includes("samantha") ||
                v.name.toLowerCase().includes("google")
            ) || naturalVoices[0];
          if (defaultVoice) {
            setSelectedVoiceUri(defaultVoice.voiceURI);
          }
        }
      }
    };

    loadVoices();
    if ("speechSynthesis" in window) {
      speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if ("speechSynthesis" in window) {
        speechSynthesis.onvoiceschanged = null;
      }
    };
  }, [selectedVoiceUri, ttsPrefsLoaded]);

  const releaseAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    speakSessionRef.current += 1;
    releaseAudio();
    if ("speechSynthesis" in window) {
      speechSynthesis.cancel();
    }
    setSpeakingMessageId(null);
    setIsSpeechPaused(false);
    setTtsLoadingMessageId(null);
    currentUtteranceRef.current = null;
  }, [releaseAudio, setSpeakingMessageId, setIsSpeechPaused, setTtsLoadingMessageId]);

  useEffect(() => {
    return () => {
      speakSessionRef.current += 1;
      releaseAudio();
      if ("speechSynthesis" in window) {
        speechSynthesis.cancel();
      }
    };
  }, [releaseAudio]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speechRate;
    }
  }, [speechRate]);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsSpeechPaused(true);
      return;
    }
    if (!("speechSynthesis" in window)) return;
    speechSynthesis.pause();
    setIsSpeechPaused(true);
  }, [setIsSpeechPaused]);

  const resume = useCallback(() => {
    if (audioRef.current) {
      void audioRef.current.play().catch(() => {
        /* interrupted resume — user can retry */
      });
      setIsSpeechPaused(false);
      return;
    }
    if (!("speechSynthesis" in window)) return;
    speechSynthesis.resume();
    setIsSpeechPaused(false);
  }, [setIsSpeechPaused]);

  const speakWithBrowser = useCallback(
    (messageId: string, text: string): boolean => {
      if (!("speechSynthesis" in window)) return false;
      speechSynthesis.cancel();
      setSpeakingMessageId(messageId);
      setIsSpeechPaused(false);

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = speechRate;

      if (selectedVoiceUri && availableVoices.length > 0) {
        const voice = availableVoices.find(
          (v: SpeechSynthesisVoice) => v.voiceURI === selectedVoiceUri
        );
        if (voice) utterance.voice = voice;
      }

      utterance.onend = () => {
        setSpeakingMessageId((prev) => (prev === messageId ? null : prev));
        setIsSpeechPaused(false);
      };
      utterance.onerror = () => {
        setSpeakingMessageId((prev) => (prev === messageId ? null : prev));
        setIsSpeechPaused(false);
      };

      currentUtteranceRef.current = utterance;
      speechSynthesis.speak(utterance);
      return true;
    },
    [availableVoices, selectedVoiceUri, speechRate, setSpeakingMessageId, setIsSpeechPaused]
  );

  const speak = useCallback(
    (messageId: string, content: string) => {
      const session = ++speakSessionRef.current;
      releaseAudio();
      if ("speechSynthesis" in window) {
        speechSynthesis.cancel();
      }
      setTtsErrorMessageId(null);
      setTtsErrorMessage(null);
      setSpeakingMessageId(null);
      setIsSpeechPaused(false);

      const text = toSpeakableText(content);
      if (!text.trim()) return;

      const voiceId = selectedTtsVoiceId || ttsVoices[0]?.voiceId || "";
      if (!voiceId) {
        if (!speakWithBrowser(messageId, text)) {
          setTtsErrorMessageId(messageId);
          setTtsErrorMessage("Text-to-speech is not available in this browser.");
        }
        return;
      }

      setTtsLoadingMessageId(messageId);
      void (async () => {
        try {
          const blob = await ttsService.synthesize(text, voiceId);
          if (speakSessionRef.current !== session) return;

          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audioUrlRef.current = url;
          audioRef.current = audio;
          audio.playbackRate = speechRate;
          audio.onended = () => {
            if (speakSessionRef.current !== session) return;
            releaseAudio();
            setSpeakingMessageId((prev) => (prev === messageId ? null : prev));
            setIsSpeechPaused(false);
          };
          audio.onerror = () => {
            if (speakSessionRef.current !== session) return;
            releaseAudio();
            setSpeakingMessageId((prev) => (prev === messageId ? null : prev));
            setIsSpeechPaused(false);
          };
          await audio.play();
          if (speakSessionRef.current !== session) return;
          setTtsLoadingMessageId(null);
          setSpeakingMessageId(messageId);
          setIsSpeechPaused(false);
        } catch (err) {
          if (speakSessionRef.current !== session) return;
          releaseAudio();
          setTtsLoadingMessageId(null);
          if (!speakWithBrowser(messageId, text)) {
            setTtsErrorMessageId(messageId);
            setTtsErrorMessage(
              err instanceof Error ? err.message : "Audio generation failed. Click to retry."
            );
          }
        }
      })();
    },
    [
      ttsVoices,
      selectedTtsVoiceId,
      speechRate,
      releaseAudio,
      speakWithBrowser,
      setSpeakingMessageId,
      setIsSpeechPaused,
      setTtsLoadingMessageId,
      setTtsErrorMessageId,
      setTtsErrorMessage,
    ]
  );

  return {
    speakingMessageId,
    isSpeechPaused,
    availableVoices,
    selectedVoiceUri,
    speechRate,
    ttsPrefsLoaded,
    ttsVoices,
    selectedTtsVoiceId,
    ttsLoadingMessageId,
    ttsErrorMessageId,
    ttsErrorMessage,
    setSpeakingMessageId,
    setIsSpeechPaused,
    setSelectedVoiceUri,
    setSpeechRate,
    setSelectedTtsVoiceId,
    speak,
    pause,
    resume,
    stop,
    // Legacy aliases for gradual migration
    elevenVoices: ttsVoices,
    selectedElevenVoiceId: selectedTtsVoiceId,
    setSelectedElevenVoiceId: setSelectedTtsVoiceId,
  };
}
